import React, { Fragment } from 'react';
import { compose, equals, omit } from 'ramda';
import { uniqueId } from 'js/utils/methods.util';
import Slider from 'js/components/common/Slider';
import { useUpdateEffect } from 'js/hooks/useUpdateEffect';
import { usePortletConfig } from 'js/components/system/Portlet';
import { Game as CommonGame, EmptyGame } from 'js/components/common/Game';
import { defaultProps, mapProps, setDisplayName, shouldUpdate, withHandlers, withState } from 'recompose';

import withGamesSlides from './withGamesSlides';
import { DISPLAY_MODE, GAME_SIZES } from './constants';

import style from './index.scss';

export const SLIDER_OPTIONS = {
    animation: {
        type: 'monoSlider'
    },
    controls: {
        arrows: { enabled: true },
        bullets: {
            enabled: true,
            position: {
                x: 'right',
                y: 'top',
                offsetX: '4em',
                offsetY: '0.5em'
            }
        },
        buttons: { enabled: false }
    }
};

export const GridPagingSlider = ({
    className = '',
    classNames = {},
    slides,
    displayMode,
    gameSize,
    setSlideRef,
    setGameRef,
    collapseAnimation,
    expandAnimation,
    slideHeight,
    sliderOptions,
    handlerTransitionEnd,
    Game,
    showGameInfo,
    swipe = false,
    isBulletsClickable,
    showFeatureIcons = true,
    onActiveSlideChange,
    showControlsOnHover = true,
    expanded,
    container
}) => {
    const sliderCls = `${className} ${gameSize === GAME_SIZES.EXTRA_SMALL ? style.extraSmall : ''}`;
    const { showViewMore } = usePortletConfig();

    const innerSliderCls =
        `${style.innerSlider}
         ${displayMode === DISPLAY_MODE.GRID ? style.grid : ''}
         ${collapseAnimation ? `${style.grid} ${style.collapseAnimation}` : ''}
         ${expandAnimation ? style.expandAnimation : ''}`;

    const gameCls = `${style.game} ${classNames.game || ''}`;

    const innerSliderStyles = slideHeight != null
        ? {
            height: `${displayMode !== DISPLAY_MODE.GRID
                ? slideHeight
                : collapseAnimation
                    ? slideHeight
                    : slideHeight * slides.length}px`
        }
        : {};

    return (
        <Slider
            options={sliderOptions}
            isBulletsClickable={isBulletsClickable}
            rotation={false}
            swipe={swipe}
            showControls={displayMode === DISPLAY_MODE.PAGING}
            renderAll={displayMode === DISPLAY_MODE.GRID}
            showControlsOnHover={showControlsOnHover}
            className={sliderCls}
            classNames={{
                slide: style.slide,
                sliderContainer: style.sliderContainer,
                slider: innerSliderCls,
                arrows: style.arrows,
                bullet: style.bullet,
                bullets: style.bullets,
                activeBullet: style.activeBullet
            }}
            styles={{ slider: innerSliderStyles }}
            handlerSliderTransitionEnd={handlerTransitionEnd}
            refs={{ slide: setSlideRef }}
            isAllItemsVisible={expanded || !showViewMore}
            onActiveSlideChange={onActiveSlideChange}>
            {slides.map((slide, i) => (
                <Fragment key={`${slide.length}_${i}_${slides[0][0]}`}>
                    {slide.map((code, index) => code
                        ? <Game key={code}
                            {...(i === 0 && index === 0 && { setGameRef })}
                            code={code}
                            className={gameCls}
                            container={container}
                            showGameInfo={showGameInfo}
                            showFeatureIcons={showFeatureIcons}
                        />
                        : <EmptyGame key={uniqueId(index)} className={gameCls} />
                    )}
                </Fragment>
            ))}
        </Slider>
    );
};

export default compose(
    setDisplayName('GridPagingSlider'),
    defaultProps({
        isVisible: true,
        Game: CommonGame,
        sliderOptions: SLIDER_OPTIONS
    }),
    shouldUpdate((props, nextProps) => {
        const { gameCodes, expanded, isVisible } = nextProps;

        return isVisible && (
            isVisible !== props.isVisible
            || expanded !== props.expanded
            || !equals(gameCodes, props.gameCodes)
        );
    }),
    withGamesSlides,
    withState('expandAnimation', 'setExpandAnimation', false),
    withState('collapseAnimation', 'setCollapseAnimation', false),
    withState('slideHeight', 'setSlideHeight', null),
    withState('resizeObserver', 'setResizeObserver',
        ({ setSlideHeight }) => new ResizeObserver((entries) => {
            const entry = entries[0];

            setSlideHeight(
                entry
                    ? entry.target.getBoundingClientRect().height
                    : null
            );
        })),
    withHandlers(({ setSlideRef }) => ({
        setSlideRef: ({ resizeObserver }) => (ref) => {
            if (setSlideRef) {
                setSlideRef(ref);
            }

            if (ref != null) {
                resizeObserver.observe(ref);
            } else {
                resizeObserver.disconnect();
            }
        },
        handlerTransitionEnd: props => e =>
            e.target === e.currentTarget && props.expanded && props.scrollIntoView()
    })),
    Wrapped => (props) => {
        const {
            setExpandAnimation,
            setCollapseAnimation,
            expanded
        } = props;

        useUpdateEffect(() => {
            if (expanded) {
                setExpandAnimation(true);

                setTimeout(() => setExpandAnimation(false), 0);
            } else {
                setCollapseAnimation(true);
                setTimeout(() => setCollapseAnimation(false), 330);
            }
        }, [expanded]);

        return <Wrapped {...props} />;
    },
    mapProps(omit([
        'resizeObserver',
        'setResizeObserver',
        'setExpandAnimation',
        'setCollapseAnimation',
        'setSlideHeight'
    ]))
)(GridPagingSlider);
